import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, SafeAreaView, KeyboardAvoidingView, Platform, TouchableOpacity, Alert, ScrollView } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { CountryPicker } from 'react-native-country-codes-picker';
import * as Localization from 'expo-localization';
import { Button } from '../components/Button';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { Ionicons } from '@expo/vector-icons';
import { firestoreService } from '../services/firestoreService';
import { validarTelefono } from '../utils/telefono';
import { avisar } from '../utils/preguntar';
import { authBridge } from '../services/authBridge';

/**
 * AGREGAR TELÉFONO (obligatorio) — Pantalla que aparece cuando un usuario
 * entra con Google (o cualquier flujo sin teléfono) y aún no tiene un número
 * en su perfil. Sin teléfono el chofer y el pasajero no pueden contactarse,
 * así que lo exigimos ANTES de dejarlo interactuar.
 *
 * Los usuarios que se registran con correo ya ponen su teléfono en el signup,
 * por lo que nunca llegan aquí. Al guardar, persistimos en el store local y en
 * /users/{uid} (backend Admin SDK) y lo llevamos al dashboard de su rol.
 *
 * ── POR QUÉ ESTA PANTALLA TIENE AHORA UNA SALIDA ─────────────────────────
 *
 * No tenía NINGUNA: ni flecha de Atrás, ni Cerrar sesión. Y se entra con
 * `router.replace` (auth.tsx:382), que además borra el historial, así que
 * tampoco había a dónde volver. Quien llegaba aquí solo salía escribiendo un
 * número o matando la app.
 *
 * Eso es un callejón sin salida, y la guideline 2.1 de la App Store los trata
 * como app incompleta. Hasta hoy casi nadie caía aquí, porque el registro por
 * correo pide el teléfono y el de Google casi siempre trae uno.
 *
 * ⚠️ PERO SIGN IN WITH APPLE LO CAMBIA TODO: Apple NO entrega teléfono nunca,
 * y encima permite «Ocultar mi correo». O sea que a partir de ahora
 * **todo revisor de Apple que entre con Apple va a aterrizar justo aquí**, en
 * la pantalla sin salida. Al añadir el botón de Apple se convirtió un camino
 * raro en el camino PRINCIPAL del revisor.
 *
 * La salida es CERRAR SESIÓN, no saltarse el paso: el teléfono se sigue
 * exigiendo. Es decisión de producto del fundador que el chofer y el pasajero
 * tengan cómo contactarse, y una pantalla sin salida no se arregla tirando esa
 * regla a la basura — se arregla dejando que la persona pueda irse.
 */

const countryMap: Record<string, { code: string; flag: string }> = {
  DO: { code: '+1', flag: '🇩🇴' },
  US: { code: '+1', flag: '🇺🇸' },
  MX: { code: '+52', flag: '🇲🇽' },
  ES: { code: '+34', flag: '🇪🇸' },
  CO: { code: '+57', flag: '🇨🇴' },
  AR: { code: '+54', flag: '🇦🇷' },
  CL: { code: '+56', flag: '🇨🇱' },
  PE: { code: '+51', flag: '🇵🇪' },
  VE: { code: '+58', flag: '🇻🇪' },
  EC: { code: '+593', flag: '🇪🇨' },
  PR: { code: '+1', flag: '🇵🇷' },
};

// Mismo mapeo de rol → landing que usa auth.tsx tras el login.
function routeForRole(role: string | null | undefined): string {
  switch (role) {
    case 'RENT_CAR_OWNER':
      return '/(tabs)/dashboard';
    case 'CAR_RENTER':
      return '/(tabs)/marketplace';
    case 'SCHOOL_DIRECTOR':
      return '/school-director-dashboard';
    case 'RESTAURANT_OWNER':
      return '/restaurant-dashboard';
    case 'FOOD_DELIVERY':
      return '/(tabs)/food';
    case 'PASSENGER':
    case 'DRIVER':
    case 'SCHOOL_DRIVER':
    case 'DELIVERY_DRIVER':
      return '/(tabs)/map';
    default:
      return '/(tabs)/map';
  }
}

export default function AddPhoneScreen() {
  const router = useRouter();
  /**
   * MODO «COMPLETA TU PERFIL» — se entra con /add-phone?completar=1.
   *
   * Lo manda auth.tsx cuando una cuenta de Google ACABA de nacer: Google trae
   * el nombre y la foto de su cuenta —que puede ser una marca y no una
   * persona— y no trae telefono. Quien se registra por el camino normal si
   * escribe sus datos; el de Google se los salta.
   *
   * Sin el parametro, esta pantalla es exactamente la de siempre: solo el
   * telefono. Los demas caminos que llegan aqui no cambian.
   */
  const { completar } = useLocalSearchParams<{ completar?: string }>();
  const modoCompletar = String(completar ?? '') === '1';

  const [phone, setPhone] = useState('');
  // Pre-escrito con lo que trajo Google, para que solo haya que corregirlo.
  const [nombre, setNombre] = useState(() => useAppStore.getState().userName || '');
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [countryCode, setCountryCode] = useState('+1');
  const [countryFlag, setCountryFlag] = useState('🇩🇴');

  useEffect(() => {
    const region = Localization.getLocales()[0]?.regionCode;
    if (region && countryMap[region]) {
      setCountryCode(countryMap[region].code);
      setCountryFlag(countryMap[region].flag);
    }
  }, []);

  const handleSave = async () => {
    // ══════════════════════════════════════════════════════════════════════
    // AQUÍ SOLO SE COMPRUEBA LA FORMA. NO SE VERIFICA QUE EL NÚMERO EXISTA.
    // ══════════════════════════════════════════════════════════════════════
    //
    // El fundador, 4-oct-2026: «el pasajero puede agregar un número de
    // teléfono a lo loco y el sistema lo lee como si es real; o sea cuando uno
    // ya está dentro de cualquier perfil iniciado con Google».
    //
    // Tenía razón en las dos mitades, y son problemas distintos:
    //
    //   1. LA FORMA. Lo que había era `length < 10 || length > 15` y nada más,
    //      así que `0000000000` y `1111111111` entraban. Eso se cierra aquí,
    //      con el MISMO criterio que el teléfono del negocio (src/utils/
    //      telefono.ts): un solo sitio decide qué es un teléfono.
    //
    //   2. QUE SEA SUYO. Eso NO se cierra aquí y hay que decirlo claro: para
    //      saber que el número es de esta persona hay que mandarle un SMS, y
    //      eso cuesta dinero por mensaje. Hoy este número se escribe directo
    //      en Firestore con `updateUserProfile`, sin pasar por Firebase Auth.
    //
    // LO QUE ESO SEGUNDO CUESTA, Y NO ES SOLO QUE EL CHOFER NO PUEDA LLAMAR:
    // el teléfono es una de las cuatro llaves de identidad del antifraude
    // (`clavesDeIdentidad`, functions/src/loyalty.ts), y esa función lo lee de
    // **Firebase Auth** (`cuenta.phoneNumber`), no de este documento. Como
    // este camino nunca toca Auth, el número que la app llama «obligatorio»
    // NUNCA llega a ser llave. O sea que el tope de los 5 viajes y el candado
    // del viaje gratis se apoyan en una llave menos de las que parece.
    //
    // Enlazarlo de verdad se puede hacer sin recompilar el APK
    // (`@react-native-firebase/auth` ya está dentro y trae
    // `linkWithPhoneNumber`), pero es una decisión del fundador por el coste
    // del SMS. Mientras no se tome, este comentario explica por qué el número
    // de aquí no cuenta como llave.
    const nombreLimpio = nombre.trim();
    if (modoCompletar) {
      // El minimo de 3 es el mismo que exige el servidor al aprobar la cedula
      // (functions/src/kyc.ts), y el tope de 120 lo exigen las reglas: sin
      // comprobarlo aqui, el guardado devolveria un permission-denied mudo.
      if (nombreLimpio.length < 3) {
        avisar('Falta tu nombre', 'Escribe tu nombre completo, como aparece en tu cédula.');
        return;
      }
      if (nombreLimpio.length > 120) {
        avisar('Nombre muy largo', 'Tu nombre no puede pasar de 120 caracteres.');
        return;
      }
    }

    const tel = validarTelefono(phone);
    if (!tel.ok) {
      Alert.alert('Revisa el número', tel.motivo);
      return;
    }
    const sanitized = tel.digitos;
    setLoading(true);
    const fullPhone = `${countryCode}${sanitized}`;
    try {
      const store = useAppStore.getState();
      // En modo «completa tu perfil» tambien entra el nombre que escribio.
      store.setProfile(
        modoCompletar ? nombreLimpio : (store.userName || ''),
        fullPhone,
        store.userEmail || ''
      );

      // MIGRADO DE RAILWAY → FIRESTORE DIRECTO.
      // Antes esto iba al backend de Railway, que está BORRADO, y el fallo se
      // tragaba con `.catch(() => {})`. El teléfono quedaba SOLO en el store
      // local: al reinstalar la app o cambiar de teléfono, se perdía — y el
      // chofer no podía ser contactado para el viaje.
      //
      // `role` y `baseRole` NO se envían: están bloqueados al cliente en
      // firestore.rules (nadie se auto-asigna rol). El rol lo fija la Cloud
      // Function `setDriverRole` o el registro. Aquí solo va el teléfono,
      // que sí es del usuario.
      const uid = store.userFirebaseUid;
      if (uid) {
        try {
          // `name` solo en modo completar y solo si de verdad cambio: las
          // reglas lo deniegan en cuanto la cedula lo cierra
          // (`nombreVerificado`), y mandarlo de mas seria un error seguro.
          const cambios: Record<string, any> = { phone: fullPhone };
          if (modoCompletar && nombreLimpio && nombreLimpio !== store.userName) {
            cambios.name = nombreLimpio;
          }
          await firestoreService.updateUserProfile(uid, cambios);
        } catch (persistErr: any) {
          // No bloqueamos: el número ya quedó en el store y el usuario puede
          // seguir. Pero avisamos, en vez de fallar en silencio como antes.
          console.warn('[add-phone] no se pudo persistir el teléfono:', persistErr?.message);
          Alert.alert(
            'Guardado solo en este teléfono',
            'Tu número quedó en la app pero no llegó al servidor. Revisa tu conexión y vuelve a guardarlo desde Perfil → Editar Perfil.'
          );
        }
      }

      setLoading(false);
      // Con el perfil completo, a elegir como quiere usar la app — si todavia
      // no lo eligio. Desde ahi, quien toque «Quiero Conducir» cae derecho en
      // la verificacion, que es lo que pidio el fundador: el chofer se
      // verifica de inmediato porque tiene que dar los datos del vehiculo.
      const rolActual = useAppStore.getState().userRole;
      router.replace((modoCompletar && !rolActual ? '/role-selection' : routeForRole(rolActual)) as any);
    } catch (e: any) {
      setLoading(false);
      Alert.alert('Error', 'No pudimos guardar tu número. Intenta de nuevo.');
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        {/* LA SALIDA. Ver la nota de arriba: sin esto la pantalla es un
            callejón sin salida, y el camino del revisor de Apple pasa por
            aquí. */}
        <TouchableOpacity
          style={estiloSalida.boton}
          onPress={async () => {
            // Si hay historial, basta con volver. Pero entrando por
            // `router.replace` normalmente NO lo hay, y entonces la única
            // salida honesta es cerrar la sesión.
            if (router.canGoBack()) {
              router.back();
              return;
            }
            try {
              await authBridge.signOut();
            } catch (e: any) {
              console.warn('[add-phone] signOut falló:', e?.message);
            }
            router.replace('/');
          }}
          accessibilityRole="button"
          accessibilityLabel="Salir y cerrar sesión"
        >
          <Ionicons name="arrow-back" size={20} color={Colors.textSecondary} />
          <Text style={estiloSalida.texto}>Salir</Text>
        </TouchableOpacity>

        <View style={styles.header}>
          <View style={styles.iconCircle}>
            <Ionicons name="call" size={30} color={Colors.white} />
          </View>
          <Text style={styles.title}>
            {modoCompletar ? 'Completa tu perfil' : 'Agrega tu teléfono'}
          </Text>
          <Text style={styles.subtitle}>
            {modoCompletar
              ? 'Solo dos datos y entras. Tu nombre, para que el chofer sepa a quién recoge, y tu teléfono para que puedan contactarse.'
              : `Lo necesitamos para que tú y \ntu chofer (o pasajero) puedan contactarse durante el viaje o pedido.`}
          </Text>
        </View>

        {/* ── ESTA PANTALLA NECESITA SCROLL ──────────────────────────
            Con el campo del nombre del modo «completa tu perfil», el boton
            «Guardar y continuar» se sale de la pantalla en un telefono
            normal — y sin scroll no hay forma de llegar a el: la pantalla
            deja de poder completarse. Lo cazo la revision adversarial.
            `keyboardShouldPersistTaps` para que el primer toque en el boton
            cuente aunque el teclado este abierto, en vez de solo cerrarlo. */}
        <ScrollView
          contentContainerStyle={{ paddingBottom: 32 }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
        <View style={styles.form}>
          {modoCompletar && (
            <>
              <Text style={styles.label}>Tu nombre completo</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  placeholder="Como aparece en tu cédula"
                  value={nombre}
                  onChangeText={setNombre}
                  placeholderTextColor={Colors.textSecondary}
                  autoCapitalize="words"
                  maxLength={120}
                />
              </View>
              <View style={{ height: 14 }} />
            </>
          )}
          <Text style={styles.label}>Número de Celular</Text>
          <View style={styles.inputContainer}>
            <TouchableOpacity style={styles.countryPickerContainer} onPress={() => setShowPicker(true)}>
              <Text style={styles.flag}>{countryFlag}</Text>
              <Text style={styles.prefix}>{countryCode}</Text>
              <Ionicons name="chevron-down" size={14} color={Colors.textSecondary} style={{ marginLeft: 4 }} />
            </TouchableOpacity>
            <TextInput
              style={styles.input}
              placeholder="(555) 000-0000"
              keyboardType="phone-pad"
              value={phone}
              onChangeText={(text) => setPhone(text.replace(/[^0-9+() -]/g, ''))}
              placeholderTextColor={Colors.textSecondary}
              maxLength={18}
              autoFocus={!modoCompletar}
            />
          </View>

          <View style={styles.noteRow}>
            <Ionicons name="lock-closed" size={14} color={Colors.textSecondary} style={{ marginRight: 6 }} />
            <Text style={styles.noteText}>
              Tu número solo se comparte con la contraparte de un viaje/pedido activo.
            </Text>
          </View>

          <Button title="Guardar y continuar" onPress={handleSave} loading={loading} style={styles.button} />
        </View>
        </ScrollView>

        <CountryPicker
          show={showPicker}
          pickerButtonOnPress={(item) => {
            setCountryCode(item.dial_code);
            setCountryFlag(item.flag);
            setShowPicker(false);
          }}
          lang="es"
          onBackdropPress={() => setShowPicker(false)}
          style={{ modal: { height: 500 } }}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: Colors.primary },
  container: { flex: 1 },
  header: {
    paddingTop: Platform.OS === 'ios' ? 8 : 38,
    paddingBottom: 24,
    alignItems: 'center',
    paddingHorizontal: SIZES.padding,
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: { fontSize: 26, fontWeight: '900', color: Colors.white, letterSpacing: -0.5 },
  subtitle: {
    color: Colors.background,
    fontSize: SIZES.medium,
    marginTop: 10,
    opacity: 0.9,
    textAlign: 'center',
    lineHeight: 21,
  },
  form: {
    flex: 1,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    padding: SIZES.padding * 1.5,
  },
  label: { fontSize: 14, color: Colors.text, marginBottom: 8, fontWeight: '600' },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background,
    borderRadius: SIZES.radius,
    borderWidth: 1,
    borderColor: Colors.border,
    height: 56,
    paddingHorizontal: 12,
    marginBottom: 16,
  },
  countryPickerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    height: '100%',
    borderRightWidth: 1,
    borderRightColor: Colors.border,
    marginRight: 12,
  },
  flag: { fontSize: 20, marginRight: 4 },
  prefix: { fontSize: 16, fontWeight: 'bold', color: Colors.text },
  input: { flex: 1, fontSize: 16, color: Colors.text, height: '100%' },
  noteRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 24 },
  noteText: { flex: 1, fontSize: 12, color: Colors.textSecondary, lineHeight: 17 },
  button: { marginTop: 8 },
});

// Aparte del StyleSheet de la pantalla a propósito: así se ve de un vistazo
// qué se añadió y qué estaba desde antes.
const estiloSalida = StyleSheet.create({
  boton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: SIZES.padding,
    // 44 px de alto: el mínimo táctil que pide Apple en sus guías de
    // interfaz, y lo que hace que el botón se pueda tocar de verdad.
    height: 44,
    marginTop: Platform.OS === 'ios' ? 36 : 28,
  },
  texto: {
    marginLeft: 6,
    fontSize: 15,
    fontWeight: '600',
    color: Colors.textSecondary,
  },
});
